import { AxiosInstance } from 'axios';
import { APP_CONFIG } from '../../constants/APP_CONFIG';

type JSONFormDataEntry = {
  name: string;
  base64EncodedValue: string;
  filename?: string;
  filepath?: string;
  contentType?: string;
};
type JSONFormData = JSONFormDataEntry[];

/**
 * This is a work-around for an issue with serverless-offline where it does
 * not respect the binaryMediaTypes defined in the API Gateway section of the
 * serverless.yml
 *
 * Because of this lack of support, when running in development (localhost), we translate
 * multipart/form-data payloads into this JSON equivalent using base64 encoded data instead
 * of binary data.
 *
 * In deployed environments, we use *real* binary payloads from the frontend (which are ~35% smaller)
 *
 * Issue is being tracked here: https://github.com/dherault/serverless-offline/issues/464
 */
export const convertFormDataOnLocalDev = (client: AxiosInstance) => {
  // This only applies when running local instance of the SD Gateway
  if (!APP_CONFIG.services.sdGateway.baseUrl.includes('localhost')) return;

  client.interceptors.request.use(async config => {
    if (!(config.data instanceof FormData)) return config;
    // eslint-disable-next-line no-console
    console.warn(
      [
        '-----------------------------------------------',
        `${config.method} ${config.url}`,
        '-----------------------------------------------',
        'INFO: Converting request with FormData to JSON payload for local SD Gateway instance',
        '-----------------------------------------------'
      ].join('\n')
    );
    let jsonFormData: JSONFormData = [];
    const formDataEntries = Array.from(config.data.entries()).map(([name, value]) => ({ name, value }));
    for (const { name, value } of formDataEntries) {
      if (value instanceof File) {
        const base64File = await new Promise<JSONFormDataEntry>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(value);
          reader.addEventListener('load', () => {
            const result = reader.result as string;
            resolve({
              name,
              filename: value.name,
              contentType: value.type,
              // Chop off the base64 URI header
              base64EncodedValue: result.slice(result.indexOf('base64,') + 7)
            });
          });
          reader.addEventListener('error', error => reject(error));
        });
        jsonFormData = [...jsonFormData, base64File];
      } else {
        jsonFormData = [...jsonFormData, { name, base64EncodedValue: btoa(value) }];
      }
    }
    return {
      ...config,
      headers: {
        // Remove existing content-type header
        ...Object.entries(config.headers).reduce((headers, [key, value]) =>
          key.toLowerCase() === 'content-type' ? headers : { ...headers, [key]: value }
        ),
        'X-Seller-Dashboard-Gateway-json-form-data': true,
        'Content-Type': 'application/json'
      },
      data: { jsonFormData }
    };
  });
};
